// src/components/Profile/BookmarksManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Bookmark,
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Sort,
  OpenInNew
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { bookmarkService } from '../../services/bookmarkService';
import BadgedAvatar from '../Common/BadgedAvatar';

const BookmarksManager = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBookmark, setSelectedBookmark] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (user?.id) {
      loadBookmarks();
    }
  }, [user?.id]);

  const loadBookmarks = async () => {
    try {
      setLoading(true);
      const response = await bookmarkService.getUserBookmarks(user.id);
      setBookmarks(response.items || []);
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to load bookmarks'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditBookmark = (bookmark) => {
    setSelectedBookmark(bookmark);
    setEditNotes(bookmark.notes || '');
    setEditTags(bookmark.tags ? bookmark.tags.join(', ') : '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBookmark) return;

    try {
      const tags = editTags.split(',').map(tag => tag.trim()).filter(tag => tag);
      await bookmarkService.updateBookmark(selectedBookmark.id, editNotes, tags);
      
      setBookmarks(prev => prev.map(bookmark => 
        bookmark.id === selectedBookmark.id 
          ? { ...bookmark, notes: editNotes, tags }
          : bookmark
      ));
      
      setEditDialogOpen(false);
      addNotification({
        type: 'success',
        message: 'Bookmark updated successfully'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to update bookmark'
      });
    }
  };

  const handleRemoveBookmark = async (bookmarkId, postId) => {
    try {
      await bookmarkService.toggleBookmark(postId);
      setBookmarks(prev => prev.filter(bookmark => bookmark.id !== bookmarkId));
      addNotification({
        type: 'success',
        message: 'Bookmark removed'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to remove bookmark'
      });
    }
  };

  const filteredBookmarks = bookmarks
    .filter(bookmark => {
      const matchesSearch = !searchQuery || 
        bookmark.post?.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bookmark.notes?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesTag = !selectedTag || 
        bookmark.tags?.includes(selectedTag);
      
      return matchesSearch && matchesTag;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'author':
          return a.post?.author?.displayName?.localeCompare(b.post?.author?.displayName);
        default: // newest
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  const allTags = [...new Set(bookmarks.flatMap(bookmark => bookmark.tags || []))];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Bookmark sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" fontWeight="bold">
          My Bookmarks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ({bookmarks.length} saved)
        </Typography>
      </Box>

      {/* Search and Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search bookmarks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Sort by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
                SelectProps={{ native: true }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="author">By Author</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label="All"
                  onClick={() => setSelectedTag('')}
                  color={!selectedTag ? 'primary' : 'default'}
                  size="small"
                />
                {allTags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => setSelectedTag(tag)}
                    color={selectedTag === tag ? 'primary' : 'default'}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bookmarks List */}
      {filteredBookmarks.length === 0 ? (
        <Alert severity="info" sx={{ textAlign: 'center' }}>
          {bookmarks.length === 0 
            ? "You haven't bookmarked any posts yet. Start bookmarking posts to save them here!"
            : "No bookmarks match your current filters."
          }
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredBookmarks.map((bookmark) => (
            <Grid item xs={12} key={bookmark.id}>
              <Card sx={{ 
                transition: 'all 0.2s ease',
                '&:hover': { 
                  transform: 'translateY(-2px)',
                  boxShadow: 4 
                }
              }}>
                <CardContent>
                  {/* Post Author */}
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <BadgedAvatar 
                      user={bookmark.post?.author}
                      size={32}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {bookmark.post?.author?.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{bookmark.post?.author?.username}
                      </Typography>
                    </Box>
                    <Box ml="auto">
                      <Typography variant="caption" color="text.secondary">
                        Bookmarked {formatDistanceToNow(new Date(bookmark.createdAt))} ago
                      </Typography>
                    </Box>
                  </Box>

                  {/* Post Content */}
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {bookmark.post?.content}
                  </Typography>

                  {/* Bookmark Notes */}
                  {bookmark.notes && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'action.hover', 
                      borderRadius: 1, 
                      mb: 2,
                      borderLeft: 4,
                      borderColor: 'primary.main'
                    }}>
                      <Typography variant="body2" fontStyle="italic">
                        "{bookmark.notes}"
                      </Typography>
                    </Box>
                  )}

                  {/* Tags */}
                  {bookmark.tags && bookmark.tags.length > 0 && (
                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      {bookmark.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>

                <Divider />

                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Box>
                    <Button
                      startIcon={<OpenInNew />}
                      onClick={() => window.open(`/post/${bookmark.postID}`, '_blank')}
                      size="small"
                    >
                      View Post
                    </Button>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={() => handleEditBookmark(bookmark)}
                      size="small"
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => handleRemoveBookmark(bookmark.id, bookmark.postID)}
                      size="small"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Bookmark Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Bookmark</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            placeholder="Add your thoughts about this post..."
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Tags"
            value={editTags}
            onChange={(e) => setEditTags(e.target.value)}
            placeholder="Enter tags separated by commas"
            helperText="e.g. important, funny, inspiration"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookmarksManager;